/**
 * @author: lv.chen
 * @date: 2025/4/28
 * @desc: CustomizeTagInput自定义标签输入组件
 */
import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { Icon, Input, InputProps } from 'tea-component';
import ListRenderGuide from '@gp/components/ListRenderGuide';
import GeneralDeletableTag from '@gp/components/GeneralDeletableTag';
import { getTagInputValue, checkRepeatTags } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { IGeneralTagFormatter } from '@gp/components/GeneralTag';
import type { IDynamicFormControlValueAccessor } from '@gp/components/DynamicForm/components/FormControl/types';
import type { ICustomizeTagInputValue } from './types';

import './index.scss';

export interface ICustomizeTagInputValidation {
  invalid: boolean;
  message: string;
}

export interface ICustomizeTagInputInstance {
  getInputEl: () => HTMLInputElement | null;
  focusInputEl: () => void;
}

interface ICustomizeTagInputProps extends IBaseProps, IDynamicFormControlValueAccessor<ICustomizeTagInputValue> {
  placeholder?: string;
  inputProps?: Omit<InputProps, 'placeholder' | 'value' | 'onChange'>;
  // 是否忽略大小写（主要用于判定是否重名），默认不忽略大小写
  ignoreCase?: boolean;
  // 已存在的标签
  existedTags?: string[] | null;
  // 标签formatter方法
  formatter?: IGeneralTagFormatter;
  validateTag?: (tag: string) => ICustomizeTagInputValidation;
}

const CustomizeTagInput: React.ForwardRefRenderFunction<ICustomizeTagInputInstance, ICustomizeTagInputProps> = (
  props,
  ref,
) => {
  const {
    style,
    className,
    placeholder,
    inputProps,
    ignoreCase = false,
    existedTags,
    formatter,
    validateTag,
    value: valueProps,
    onChange,
  } = props;
  const {
    className: inputClassName,
    onPressEnter: onPressEnterProps,
    onBlur: onBlurProps,
    onPaste: onPasteProps,
  } = inputProps || {};

  const input = useRef<HTMLInputElement>(null);

  const isPasting = useRef(false);

  const [validation, setValidation] = useState<ICustomizeTagInputValidation>({ invalid: false, message: '' });

  const [value, setValue] = useState<ICustomizeTagInputValue>(() => getTagInputValue(valueProps));

  const validateTags = (tags?: string[] | null, tag?: string): boolean => {
    if (!!tag && validateTag) {
      const { invalid, message } = validateTag(tag) || {};
      if (!!invalid) {
        setValidation({ invalid, message: message || '' });
        return true;
      }
    }
    if (checkRepeatTags(tags, tag, { ignoreCase, existedTags })) {
      setValidation({ invalid: true, message: 'This name already exists. Please choose a different one.' });
      return true;
    }
    setValidation({ invalid: false, message: '' });
    return false;
  };

  const handleInputChange = (payload: string) => {
    if (isPasting.current) {
      isPasting.current = false;
      return;
    }
    const valueTmp = { ...value, input: payload };
    setValue(valueTmp);
    validateTags(valueTmp.tags, valueTmp.input);
    onChange?.(valueTmp);
  };

  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    isPasting.current = true;
    const pasteText = e?.clipboardData?.getData('text')?.trim();
    const invalid = validateTags(value?.tags, pasteText);
    if (invalid) {
      const valueTmp = { ...value, input: pasteText };
      setValue(valueTmp);
      onChange?.(valueTmp);
    } else {
      handleAddTag(pasteText);
    }
    onPasteProps?.(e);
  };

  const handleAddTag = (payload: string) => {
    if (!payload) return;
    if (!!validation?.invalid) return;
    const { tags } = value || {};
    const tagsTmp = [...tags, payload];
    setValue({ tags: tagsTmp, input: '' });
    onChange?.({ tags: tagsTmp, input: '' });
  };

  const handleDeleteTag = (payload?: string) => {
    const tagsTmp = value?.tags?.filter((tag) => {
      return tag !== payload;
    });
    const valueTmp = { ...value, tags: tagsTmp || [] };
    setValue(valueTmp);
    validateTags(valueTmp.tags, valueTmp.input);
    onChange?.(valueTmp);
  };

  const handleDeleteAllTags = () => {
    const valueTmp = { ...value, tags: [], input: '' };
    setValue(valueTmp);
    validateTags(valueTmp.tags, valueTmp.input);
    onChange?.(valueTmp);
  };

  useEffect(() => {
    setValue(getTagInputValue(valueProps));
  }, [valueProps]);

  useImperativeHandle(ref, () => ({
    getInputEl: () => {
      return input.current || null;
    },
    focusInputEl: () => {
      input.current?.focus();
    },
  }));

  return (
    <div
      style={style}
      className={classnames('dbg-customize-tag-input', className, {
        'dbg-customize-tag-input--error': !!validation?.invalid,
      })}
    >
      <div className="dbg-customize-tag-input__control">
        <ListRenderGuide list={value?.tags}>
          <ul className="dbg-customize-tag-input__tag-list">
            {value?.tags?.map((tag) => {
              return (
                <li key={tag} className="dbg-customize-tag-input__tag-item">
                  <GeneralDeletableTag text={tag} formatter={formatter} onDelete={handleDeleteTag} />
                </li>
              );
            })}
          </ul>
        </ListRenderGuide>
        <div className="dbg-customize-tag-input__input-wrapper">
          <Input
            {...inputProps}
            ref={input}
            className={classnames('dbg-customize-tag-input__input', inputClassName)}
            placeholder={value?.tags?.length > 0 ? '' : placeholder}
            value={value?.input}
            onChange={handleInputChange}
            onPressEnter={(payload, e) => {
              e?.stopPropagation();
              handleAddTag(payload?.trim() || '');
              onPressEnterProps?.(payload, e);
            }}
            onBlur={(e) => {
              e?.stopPropagation();
              handleAddTag(value?.input?.trim() || '');
              onBlurProps?.(e);
            }}
            onPaste={handleInputPaste}
          />
          {(!!value?.tags?.length || value?.input) && (
            <Icon
              className="dbg-customize-tag-input__delete-btn"
              type="dismiss"
              onClick={(e) => {
                e?.stopPropagation();
                handleDeleteAllTags();
              }}
            />
          )}
        </div>
      </div>
      {!!validation?.invalid && <div className="dbg-customize-tag-input__error-tips">{validation?.message}</div>}
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(CustomizeTagInput);
